import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

/**
 * La coque des deux espaces : celui de l'entreprise et celui de l'école.
 *
 * La navigation est le SEUL grand aplat de couleur de l'application, et c'est
 * délibéré : c'est ce qui fait qu'une entreprise voit l'école avant de lire un
 * mot. Le formulaire public, lui, reste blanc — l'identité y passe par
 * l'illustration.
 *
 * Elle prend ses entrées en paramètre. L'espace école avait sa propre coque, un
 * bandeau d'aplat et des onglets en pilules blanches, alors que l'aperçu de sa
 * propre couleur (`NavColorField`) montrait CETTE barre-ci : le référent réglait
 * une barre qu'il ne voyait nulle part chez lui, et la pastille de compte s'écrivait
 * deux fois, de deux couleurs. Une coque, une pastille.
 *
 * Sous 1024px la barre latérale devient une rangée plutôt qu'un tiroir : un tiroir
 * demande un geste de plus pour changer d'écran. Sous 768px, la rangée devient une
 * grille où chaque entrée prend sa part de la largeur, icône au-dessus d'un libellé
 * court, 44px de haut : en défilement horizontal, la dernière entrée sortait de
 * l'écran (« Mon entreprise » invisible, « Ma page de dépôt » coupée en 390).
 */

export type EntreeNav = {
	to: string;
	/** `true` pour l'accueil d'un espace, qui sinon resterait sélectionné partout. */
	end?: boolean;
	label: string;
	/** Le libellé sous l'icône, sous 768px : un mot, deux au plus. */
	shortLabel: string;
	icon: LucideIcon;
	/** Ce qui attend un geste. Rien sous un : un zéro ne se lit pas. */
	badge?: number;
};

/** Écrites en entier : Tailwind ne voit pas une classe composée à l'exécution. */
const COLONNES: Record<number, string> = {
	2: "grid-cols-2",
	3: "grid-cols-3",
	4: "grid-cols-4",
};

/**
 * La pastille de compte. Elle s'INVERSE au lieu de porter une teinte fixe.
 *
 * Elle était orange Mentor Goal, ce qui ressortait très bien sur l'indigo par
 * défaut et disparaissait sur la barre d'ACE, dont la couleur d'école est un
 * orange. En reprenant le couple texte/fond de la barre à l'envers, elle se
 * détache par construction, quelle que soit la couleur choisie.
 */
function Compte({ valeur, className }: { valeur: number; className: string }) {
	return (
		<span
			className={`t-caption-strong rounded-full bg-mg-nav-fg px-1.5 tabular-nums text-mg-nav ${className}`}
		>
			{valeur}
		</span>
	);
}

export function EspaceLayout({
	navLabel,
	logoUrl,
	name,
	caption,
	picker,
	entries,
	user,
	actions,
	alert,
	children,
}: {
	/** Le nom de la navigation pour un lecteur d'écran (« Espace école »). */
	navLabel: string;
	logoUrl?: string | null;
	/** Le nom de l'école, en tête de la barre. */
	name: string;
	/** La ligne sous le nom (« Espace partenaire », « Espace école »). */
	caption: string;
	/** S'ajoute sous le nom, sur sa propre rangée, quand la personne a plusieurs écoles. */
	picker?: ReactNode;
	entries: readonly EntreeNav[];
	/** Qui est connecté, à gauche du bandeau blanc. */
	user: ReactNode;
	/** Les gestes du bandeau blanc, à droite : déposer, se déconnecter. */
	actions: ReactNode;
	/** Un échec qui concerne tout l'espace, sous le bandeau. */
	alert?: ReactNode;
	children: ReactNode;
}) {
	return (
		<div className="flex min-h-screen flex-col bg-mg-page lg:flex-row">
			{/*
			 * L'aplat descend jusqu'en bas, le contenu reste collé en haut.
			 *
			 * `h-screen` sur la barre elle-même la coupait à la hauteur de l'écran : sur
			 * une page longue, la colonne de gauche s'arrêtait au milieu et laissait un
			 * blanc. C'est l'aside qui s'étire (comportement par défaut d'une rangée
			 * flex), et son contenu qui devient collant.
			 */}
			<aside className="flex shrink-0 flex-col bg-mg-nav text-mg-nav-fg lg:w-64">
				<div className="flex flex-col lg:sticky lg:top-0 lg:h-screen">
					<div className="flex items-center gap-3 px-5 py-3 lg:py-6">
						{logoUrl ? (
							<img
								src={logoUrl}
								alt=""
								className="h-9 w-9 shrink-0 rounded-lg bg-mg-surface object-contain p-1"
							/>
						) : (
							<span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-mg-nav-selected t-label">
								{name.slice(0, 2).toUpperCase()}
							</span>
						)}
						<div className="min-w-0 flex-1">
							{/* Le nom reste lisible, même long, et même quand on peut en changer :
							    c'est ce qui dit au référent d'un groupe où il se trouve. Deux lignes en
							    390 ; entier sur PC, où la barre latérale ne laisse que 170 px de
							    large et que le campus qui distingue deux écoles est en fin de nom. */}
							<p className="t-label line-clamp-2 break-words lg:line-clamp-none">{name}</p>
							<p className="t-caption truncate text-mg-nav-fg-muted">{caption}</p>
						</div>
					</div>
					{picker ? <div className="px-5 pb-3">{picker}</div> : null}

					<nav
						aria-label={navLabel}
						className={`grid ${COLONNES[entries.length] ?? "grid-cols-4"} gap-1 px-3 pb-3 md:flex md:overflow-x-auto lg:flex-1 lg:flex-col lg:overflow-visible lg:pb-0`}
					>
						{entries.map((item) => (
							<NavLink
								key={item.to}
								to={item.to}
								end={item.end}
								className={({ isActive }) =>
									`flex min-h-11 min-w-0 flex-col items-center justify-center gap-0.5 rounded-field px-1 py-1.5 transition-colors md:shrink-0 md:flex-row md:justify-start md:gap-2.5 md:px-3 md:py-2.5 ${
										isActive
											? "bg-mg-nav-selected text-mg-nav-fg"
											: "text-mg-nav-fg-muted hover:bg-mg-nav-hover hover:text-mg-nav-fg"
									}`
								}
							>
								<span className="relative flex">
									<item.icon className="size-[18px] shrink-0" aria-hidden="true" />
									{item.badge ? (
										<Compte valeur={item.badge} className="absolute -top-1.5 left-3 md:hidden" />
									) : null}
								</span>
								<span className="t-caption-strong max-w-full truncate md:hidden">
									{item.shortLabel}
								</span>
								<span className="t-label hidden whitespace-nowrap md:inline">{item.label}</span>
								{item.badge ? (
									<Compte valeur={item.badge} className="ml-auto hidden md:inline" />
								) : null}
							</NavLink>
						))}
					</nav>

					<div className="hidden px-5 pb-5 lg:block">
						<p className="t-caption text-mg-nav-fg-muted">Propulsé par Mentor Goal</p>
					</div>
				</div>
			</aside>

			<div className="flex min-w-0 flex-1 flex-col">
				<header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-mg-border bg-mg-surface px-5 py-2 md:py-3">
					<div className="flex min-w-0 items-center gap-2.5">{user}</div>
					<div className="flex shrink-0 items-center gap-2">{actions}</div>
				</header>

				{alert}

				<main className="mx-auto w-full max-w-5xl flex-1 px-5 py-6">{children}</main>
			</div>
		</div>
	);
}
